import { useEffect, useState } from "react";
import api, { fmtDT } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import { CalendarClock } from "lucide-react";
import { Badge } from "@/components/ui/badge";

const today = () => new Date().toISOString().slice(0, 10);
const SLOTS = ["09:00","09:30","10:00","10:30","11:00","11:30","12:00","12:30","14:00","14:30","15:00","15:30","16:00","16:30","17:00","17:30","18:00","18:30","19:00","19:30"];

const monthMatrix = (date) => {
  const y = date.getFullYear(), m = date.getMonth();
  const first = new Date(y, m, 1);
  const startDow = first.getDay();
  const days = new Date(y, m + 1, 0).getDate();
  const cells = [];
  for (let i = 0; i < startDow; i++) cells.push(null);
  for (let i = 1; i <= days; i++) cells.push(new Date(y, m, i));
  return cells;
};

export default function Appointments() {
  const [rows, setRows] = useState([]);
  const [patients, setPatients] = useState([]);
  const [doctors, setDoctors] = useState([]);
  const [selected, setSelected] = useState(today());
  const [monthDate, setMonthDate] = useState(new Date());
  const [form, setForm] = useState({ patient_id: "", doctor_id: "", date: today(), time: "10:00" });

  const load = () => api.get("/appointments").then((r) => setRows(r.data));
  useEffect(() => { load(); api.get("/patients").then((r) => setPatients(r.data)); api.get("/doctors").then((r) => setDoctors(r.data)); }, []);

  const takenSlots = new Set(rows.filter((r) => r.date === form.date && r.doctor_id === form.doctor_id).map((r) => r.time));

  const create = async () => {
    if (!form.patient_id || !form.doctor_id) return toast.error("Select patient and doctor");
    try {
      await api.post("/appointments", { ...form, date: selected });
      toast.success("Appointment booked");
      load();
    } catch { toast.error("Failed"); }
  };

  const cells = monthMatrix(monthDate);
  const monthLabel = monthDate.toLocaleDateString("en-IN", { month: "long", year: "numeric" });
  const dayApptCount = (d) => rows.filter((r) => r.date === (d ? d.toISOString().slice(0, 10) : "")).length;

  const selectedRows = rows.filter((r) => r.date === selected);

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="font-display text-2xl font-semibold">Appointments</h1>
          <p className="text-sm text-slate-500">Book and view appointments day-by-day.</p>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
        {/* Mini calendar */}
        <div className="bg-white border p-4">
          <div className="flex items-center justify-between mb-3">
            <button onClick={() => setMonthDate(new Date(monthDate.getFullYear(), monthDate.getMonth() - 1, 1))} className="text-slate-500 hover:text-slate-900">‹</button>
            <div className="font-display font-semibold">{monthLabel}</div>
            <button onClick={() => setMonthDate(new Date(monthDate.getFullYear(), monthDate.getMonth() + 1, 1))} className="text-slate-500 hover:text-slate-900">›</button>
          </div>
          <div className="grid grid-cols-7 gap-1 text-center text-[10px] text-slate-500 mb-1">
            {["S","M","T","W","T","F","S"].map((d, i) => <div key={`wd-${i}`}>{d}</div>)}
          </div>
          <div className="grid grid-cols-7 gap-1">
            {cells.map((d, i) => {
              if (!d) return <div key={`empty-${i}`} className="h-9"/>;
              const iso = d.toISOString().slice(0, 10);
              const isToday = iso === today();
              const isSel = iso === selected;
              const cnt = dayApptCount(d);
              return (
                <button
                  key={iso}
                  data-testid={`cal-${iso}`}
                  onClick={() => { setSelected(iso); setForm({ ...form, date: iso }); }}
                  className={`h-9 text-sm rounded-sm relative ${isSel ? "bg-blue-700 text-white" : isToday ? "border border-blue-500 text-blue-700 font-semibold" : "hover:bg-blue-50"}`}
                >
                  {d.getDate()}
                  {cnt > 0 && <span className={`absolute bottom-0.5 right-1 text-[9px] font-mono ${isSel ? "text-white" : "text-blue-700"}`}>{cnt}</span>}
                </button>
              );
            })}
          </div>
          <div className="mt-3 flex gap-2 text-xs items-center text-slate-500">
            <div className="w-3 h-3 border border-blue-500"/> Today
            <div className="w-3 h-3 bg-blue-700 ml-2"/> Selected
          </div>
        </div>

        {/* Booking form */}
        <div className="bg-white border p-4 lg:col-span-2 space-y-3">
          <div className="flex items-center gap-2 pb-3 border-b">
            <CalendarClock className="w-4 h-4 text-blue-700"/>
            <div className="font-display font-semibold">Book for {new Date(selected).toLocaleDateString("en-IN", { weekday: "long", day: "2-digit", month: "long", year: "numeric" })}</div>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label className="label-tiny">Patient</Label>
              <select data-testid="appt-patient" className="w-full h-9 border px-2 text-sm" value={form.patient_id} onChange={(e) => setForm({ ...form, patient_id: e.target.value })}>
                <option value="">-- Select --</option>
                {patients.map((p) => <option key={p.id} value={p.id}>{p.name} · {p.mobile}</option>)}
              </select>
            </div>
            <div>
              <Label className="label-tiny">Doctor</Label>
              <select data-testid="appt-doctor" className="w-full h-9 border px-2 text-sm" value={form.doctor_id} onChange={(e) => setForm({ ...form, doctor_id: e.target.value })}>
                <option value="">-- Select --</option>
                {doctors.map((d) => <option key={d.id} value={d.id}>{d.name} · {d.department}</option>)}
              </select>
            </div>
          </div>
          <div>
            <Label className="label-tiny">Time Slot</Label>
            <div className="grid grid-cols-6 gap-2 mt-2">
              {SLOTS.map((s) => {
                const taken = takenSlots.has(s);
                const sel = form.time === s;
                return (
                  <button
                    key={s}
                    data-testid={`slot-${s}`}
                    disabled={taken}
                    onClick={() => setForm({ ...form, time: s })}
                    className={`h-9 text-xs border rounded-sm font-mono ${taken ? "bg-slate-100 text-slate-400 cursor-not-allowed" : sel ? "border-blue-600 bg-blue-600 text-white" : "border-slate-300 hover:border-blue-500"}`}
                  >
                    {s}
                  </button>
                );
              })}
            </div>
          </div>
          <Button data-testid="book-appt" onClick={create} className="bg-blue-700 hover:bg-blue-800 rounded-sm">Book Appointment</Button>
        </div>
      </div>

      {/* Selected day's appointments */}
      <div className="bg-white border">
        <div className="p-3 border-b label-tiny">Appointments on {selected} ({selectedRows.length})</div>
        <table className="w-full text-sm">
          <thead><tr className="bg-slate-50 text-left"><th className="label-tiny p-2">Time</th><th className="label-tiny">Patient</th><th className="label-tiny">Doctor</th><th className="label-tiny">Department</th><th className="label-tiny">Status</th></tr></thead>
          <tbody>
            {selectedRows.sort((a, b) => a.time.localeCompare(b.time)).map((r) => (
              <tr key={r.id} className="border-b">
                <td className="p-2 font-mono">{r.time}</td><td>{r.patient_name}</td><td>{r.doctor_name}</td><td>{r.department}</td>
                <td><Badge className="rounded-sm">{r.status}</Badge></td>
              </tr>
            ))}
            {selectedRows.length === 0 && <tr><td colSpan="5" className="p-4 text-center text-sm text-slate-500">No appointments on this day</td></tr>}
          </tbody>
        </table>
      </div>
    </div>
  );
}
